// src/components/PhotoGallery.jsx
//
// Карусель фотографий для карточки товара (ProductView).
// Свайпы между фото, индикатор-точки внизу.
// Тап на фото — открывает FullscreenImageViewer.

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import FullscreenImageViewer from './FullscreenImageViewer';

export default function PhotoGallery({ photos, alt = '' }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const startXRef = useRef(null);
  const dxRef = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div style={{
        width: '100%', aspectRatio: '4/3',
        background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--fg-faint)',
      }}>
        <Icon name="package" size={36} stroke={1.5} />
      </div>
    );
  }

  const handleTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    if (startXRef.current === null) return;
    dxRef.current = e.touches[0].clientX - startXRef.current;
    setDragOffset(dxRef.current);
  };
  const handleTouchEnd = () => {
    const dx = dxRef.current;
    const width = containerRef.current?.clientWidth || 1;
    const threshold = Math.min(80, width * 0.2);
    if (dx > threshold && index > 0) setIndex(index - 1);
    else if (dx < -threshold && index < photos.length - 1) setIndex(index + 1);
    setDragOffset(0);
    startXRef.current = null;
    dxRef.current = 0;
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'relative', width: '100%', aspectRatio: '4/3',
          borderRadius: 'var(--r-md)', overflow: 'hidden',
          background: 'var(--bg-subtle)',
          touchAction: 'pan-y',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Слайдер */}
        <div
          style={{
            display: 'flex', height: '100%',
            transform: `translateX(calc(-${index * 100}% + ${dragOffset}px))`,
            transition: dragOffset === 0 ? 'transform 250ms ease-out' : 'none',
          }}
        >
          {photos.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={alt}
              draggable={false}
              onClick={() => setFullscreen(true)}
              style={{
                width: '100%', height: '100%', flexShrink: 0,
                objectFit: 'cover',
                cursor: 'zoom-in',
              }}
            />
          ))}
        </div>

        {/* Индикатор точек */}
        {photos.length > 1 && (
          <div style={{
            position: 'absolute', bottom: 10, left: 0, right: 0,
            display: 'flex', justifyContent: 'center', gap: 5, pointerEvents: 'none',
          }}>
            {photos.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === index ? 18 : 5, height: 5, borderRadius: 5,
                  background: i === index ? '#fff' : 'rgba(255,255,255,0.6)',
                  transition: 'all 200ms',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }}
              />
            ))}
          </div>
        )}

        {/* Счётчик в углу */}
        {photos.length > 1 && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(0,0,0,0.55)', color: '#fff',
            padding: '4px 10px', borderRadius: 12,
            fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
          }}>
            {t('products.photoOf', { current: index + 1, total: photos.length })}
          </div>
        )}
      </div>

      {fullscreen && (
        <FullscreenImageViewer
          photos={photos}
          startIndex={index}
          onClose={() => setFullscreen(false)}
        />
      )}
    </>
  );
}
